import React, { useRef, useState, useEffect, useCallback } from "react";
import { throttle } from "lodash";
import { any, array, bool, number } from "prop-types";
import { useDeviceType } from "../../customHooks/useDeviceType";

import ArrowDashIcon from "../Arrow-DashIcon";

import "./carousel.m.css";

const Carousel = ({
  children,
  itemWidth = 200,
  gap = 16,
  showDots = true,
  showButtons = false,
  maxItemWidth,
  itemMobileWidth,
}) => {
  const carouselRef = useRef(null);
  const isMobile = useDeviceType() !== "desktop";
  const itemWidthFinal = isMobile && itemMobileWidth ? itemMobileWidth : itemWidth;
  const [activeIndex, setActiveIndex] = useState(0);

  const throttledSetValue = useCallback(
    throttle((newValue) => {
      setActiveIndex(newValue);
    }, 350),
    []
  );

  const handleScroll = () => {
    if (carouselRef.current) {
      const scrollLeft = carouselRef.current.scrollLeft;
      const index = Math.round(scrollLeft / itemWidthFinal);
      throttledSetValue(index);
    }
  };

  const goToSlide = (index) => {
    if (carouselRef.current) {
      carouselRef.current.scrollTo({
        left: index * itemWidthFinal,
        behavior: "smooth",
      });
      throttledSetValue(index);
    }
  };

  useEffect(() => {
    const ref = carouselRef.current;
    if (ref) {
      ref.addEventListener("scroll", handleScroll);
      return () => ref.removeEventListener("scroll", handleScroll);
    }
  }, [itemWidthFinal]);

  const childrenArray = React.Children.toArray(children);

  return (
    <div styleName="carousel-container" className="carousel-container">
      <div
        styleName="carousel"
        ref={carouselRef}
        style={{
          gap: `${gap}px`,
        }}
      >
        {!isMobile && showButtons && (
          <>
            {activeIndex !== 0 && (
              <div styleName="nav-button back" onClick={() => goToSlide(activeIndex - 1)}>
                <span styleName="svg-back">
                  <ArrowDashIcon height="26" width="26" viewBox="0 0 26 26" />
                </span>
              </div>
            )}
            {activeIndex !== childrenArray.length - 1 && (
              <div styleName="nav-button next" onClick={() => goToSlide(activeIndex + 1)}>
                <span styleName="svg-next">
                  <ArrowDashIcon height="26" width="26" viewBox="0 0 26 26" />
                </span>
              </div>
            )}
          </>
        )}
        {childrenArray.map((child, index) => (
          <div
            key={index}
            styleName="carousel-item"
            className="carousel-item"
            style={{
              minWidth: `${itemWidthFinal}px`,
              maxWidth: isMobile && itemMobileWidth ? itemMobileWidth : maxItemWidth || "unset",
            }}
          >
            {child}
          </div>
        ))}
      </div>
      {showDots && (
        <div styleName="carousel-dots">
          {childrenArray.map((_, index) => (
            <span
              key={index}
              styleName={`carousel-dot ${index === activeIndex ? "active" : ""}`}
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

Carousel.propTypes = {
  children: any,
  items: array,
  itemWidth: number,
  itemMobileWidth: number,
  gap: number,
  showDots: bool,
  showButtons: bool,
  maxItemWidth: bool,
};

export default Carousel;
